import React, { ReactNode } from 'react';
import { Check, Info } from '@phosphor-icons/react';
import { PaymentStatusNotiProps, PaymentStatusNotiType } from 'types';
import { SpinnerLoader } from 'components/common';

const PaymentStatusNoti: React.FC<PaymentStatusNotiProps> = ({
  type = 'pending',
  title,
  subtitle,
  highlightedText,
  icon,
  className = '',
  bgColor = 'var(--tealColor700)',
}) => {
  // Determine icon based on type if not provided
  const statusIcon = icon || getIconByType(type);

  // Split subtitle to insert highlighted text
  const [beforeHighlight, afterHighlight] = highlightedText
    ? splitTextByHighlight(subtitle, highlightedText)
    : [subtitle, ''];

  return (
    <div className={`w-full py-3 px-6 rounded-lg flex items-center gap-6 ${className}`} style={{ background: bgColor }}>
      <div className='p-3 bg-white rounded-full flex-shrink-0'>
        <div className={`w-8 h-8 flex items-center justify-center text-[${bgColor}] `}>{statusIcon}</div>
      </div>

      <div className='flex-1 py-1'>
        <h3 className='text-white text-lg font-bold leading-[26px]'>{title}</h3>
        <p className='text-white text-sm leading-5'>
          {beforeHighlight}
          {highlightedText && <span className='font-bold'>{highlightedText}</span>}
          {afterHighlight}
        </p>
      </div>
    </div>
  );
};

// Helper function to get icon by status type
function getIconByType(type: PaymentStatusNotiType): ReactNode {
  switch (type) {
    case 'waiting':
      return <SpinnerLoader size='s' />;
    case 'pending':
      return <SpinnerLoader size='s' />;
    case 'warning':
      return <Info size={32} weight='bold' />;
    case 'success':
      return <Check size={32} weight='bold' />;
    case 'error':
      return <Check size={32} weight='bold' />;
    default:
      return <SpinnerLoader size='md' />;
  }
}

// Helper function to split text by highlighted portion
function splitTextByHighlight(text: string, highlight: string): [string, string] {
  const index = text.indexOf(highlight);

  if (index === -1) {
    return [text, ''];
  }

  const before = text.substring(0, index);
  const after = text.substring(index + highlight.length);

  return [before, after];
}

export default PaymentStatusNoti;
